"use client"

import { createContext, useContext, useCallback, useRef, ReactNode } from 'react'

interface MediaContextType {
    pauseAllMedia: () => void
    registerMediaElement: (id: string, element: HTMLMediaElement) => void
    unregisterMediaElement: (id: string) => void
    onMediaPlay: (id: string) => void
}

const MediaContext = createContext<MediaContextType | undefined>(undefined)

export function MediaProvider({ children }: { children: ReactNode }) {
    const mediaRegistry = useRef<Map<string, HTMLMediaElement>>(new Map())

    const pauseAllMedia = useCallback(() => {
        mediaRegistry.current.forEach((element) => {
            if (!element.paused) {
                element.pause()
            }
        })
    }, [])

    const registerMediaElement = useCallback((id: string, element: HTMLMediaElement) => {
        mediaRegistry.current.set(id, element)
    }, [])

    const unregisterMediaElement = useCallback((id: string) => {
        mediaRegistry.current.delete(id)
    }, [])

    const onMediaPlay = useCallback((id: string) => {
        // Pause all other media when one starts playing
        mediaRegistry.current.forEach((element, elementId) => {
            if (elementId !== id && !element.paused) {
                element.pause()
            }
        })
    }, [])

    return (
        <MediaContext.Provider value={{ pauseAllMedia, registerMediaElement, unregisterMediaElement, onMediaPlay }}>
            {children}
        </MediaContext.Provider>
    )
}

export function useMediaContext() {
    const context = useContext(MediaContext)
    if (!context) {
        throw new Error('useMediaContext must be used within MediaProvider')
    }
    return context
}

// Optional hook for components that may or may not be inside a MediaProvider
export function useMediaContextOptional() {
    return useContext(MediaContext)
}
